import {useEffect} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {
  dispatchCenterRequiresShift,
  getDispatchOperatorFallbackRoute,
  isDispatchCenterRouteEnabled,
  isDispatchOperatorRole,
} from "@/src/modules/core/lib/dispatch-center-policy";
import {useStoredMe} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import {loadActiveResumableFormDraft} from "@/src/modules/core/lib/form-draft";
import {hasActiveBoardSeatForProperty} from "@/src/modules/core/lib/board-seat";
import {useFormDraftResumeStore} from "@/src/modules/core/store/form-draft-resume";

import {isResidentBoardModulePath} from "../lib/resident-board-module-paths";

import {
  FREE_INSPECTION_ALLOWED_PATH,
  isPathAllowedForFreeInspectionTier,
} from "../lib/service-tier-routes";
import {PATH_KEYS, PUBLIC_PATHS, STATIC_ROUTES} from "../utils/constants";

export default function useCheckNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const property = useStoredUserProperty();
  const me = useStoredMe();
  const pendingDraft = useFormDraftResumeStore((state) => state.pending);

  useEffect(() => {
    const resumeRoute =
      pendingDraft?.route ?? loadActiveResumableFormDraft()?.route ?? null;
    if (resumeRoute && location.pathname === resumeRoute) {
      return;
    }

    const parts = location.pathname.split("/").filter(Boolean);
    /** Lowercase first segment so `STATIC_ROUTES` / `PATH_KEYS` match reliably (e.g. `/vehicle-maintenance`). */
    const pathname = parts.length === 0 ? "/" : parts[0].toLowerCase();

    if (!property || PUBLIC_PATHS.includes(pathname as (typeof PUBLIC_PATHS)[number])) {
      return;
    }

    if (
      me?.isResident &&
      isResidentBoardModulePath(location.pathname) &&
      !hasActiveBoardSeatForProperty(me, property.id)
    ) {
      navigate("/home", {replace: true});
      return;
    }

    if (property.service_tier === "free_inspection") {
      const canBypassRestriction = me?.isAdmin || me?.isSeniorSupervisor;
      if (canBypassRestriction) {
        return;
      }
      if (!isPathAllowedForFreeInspectionTier(location.pathname)) {
        navigate(FREE_INSPECTION_ALLOWED_PATH);
      }
      return;
    }

    if (STATIC_ROUTES.includes(pathname as (typeof STATIC_ROUTES)[number])) {
      if (isDispatchOperatorRole(me)) {
        const sectionKey = PATH_KEYS[pathname as keyof typeof PATH_KEYS] ?? pathname;
        if (!isDispatchCenterRouteEnabled(me, sectionKey)) {
          navigate(getDispatchOperatorFallbackRoute(me));
        }
      }
      return;
    }

    const sectionKey = PATH_KEYS[pathname as keyof typeof PATH_KEYS] ?? pathname;

    if (isDispatchOperatorRole(me) && !isDispatchCenterRouteEnabled(me, sectionKey)) {
      navigate(getDispatchOperatorFallbackRoute(me));
      return;
    }

    const isSectionEnabled = property.sections.find((t) => {
      const key = PATH_KEYS[pathname as keyof typeof PATH_KEYS];
      return t.key === key && t.granted === 1;
    });
    if (!isSectionEnabled) {
      const fallback = me?.isAdmin
        ? "/dashboard"
        : isDispatchOperatorRole(me)
          ? getDispatchOperatorFallbackRoute(me)
          : "/dars";
      navigate(fallback);
    }
  }, [property, me, me?.isAdmin, me?.isSeniorSupervisor, location.pathname, navigate, pendingDraft?.route]);
}
